/* eslint-disable max-len */
/* eslint-disable array-callback-return */
import numeral from 'numeral';
import { Cookies } from 'react-cookie';

const getCookie = (name: string) => {
  const cookies = new Cookies() || {};

  return cookies.get(name);
};

class Money {
  static forceTwoDigits(num) {
    return `0${num}`.slice(-2);
  }

  static roundedDecimal(number: number) {
    // rounds to two places
    return String(Math.round((number + Number.EPSILON) * 100) / 100);
  }

  static countDecimals(value: number) {
    if (Math.floor(value) === value) {
      return 0;
    }

    //it is understood that below logic would not work for 0.0000
    return value.toString().split('.')[1].length || 0;
  }

  static getDollars(
    number?: number | string | null,
    onlyGreaterThanZero = false,
    round = true,
    givenCurrency: string | null = null,
    givenLocale: string | null = null,
    truncated = false,
  ): string {
    if (!Money.isNumeric(number) || (number === 0 && onlyGreaterThanZero)) {
      return '--';
    }

    const currencyCode =
      givenCurrency || getCookie('stockx_selected_currency') || 'USD';

    let locale = givenLocale || getCookie('language_code') || 'en';

    locale = locale.replace('_', '-');

    if (currencyCode === 'AED') {
      locale = 'US';
    }

    // fix for https://stockx-services.atlassian.net/browse/PWEB-870
    if (locale === 'fr-ca') {
      locale = 'fr';
    }

    const roundedNum = round ? Math.round(Number(number || 0)) : number;
    const digits = round ? 0 : 2;

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: digits,
    });

    if (truncated) {
      return Money.getTruncated(number, {
        currencyCode,
        locale,
      });
    }

    if (roundedNum && roundedNum > 999999999) {
      return formatter.format(999999999);
    }

    return formatter.format(Number(roundedNum || 0));
  }

  static getCurrencySymbol(givenCurrency = null, givenLocale = null) {
    const currencyCode: string =
      givenCurrency || getCookie('stockx_selected_currency') || 'USD';

    let locale = givenLocale || getCookie('stockx_selected_locale') || 'US';

    locale = locale.replace('_', '-');

    if (currencyCode === 'AED') {
      locale = 'US';
    }

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    });

    return formatter.formatToParts(0).find(({ type }) => type === 'currency')
      ?.value;
  }

  /**
   * Input: 100, $, en-US                 => Output: $100
   * Input: 3200, €, ja-JP                => Output: €3.2K
   * Input: 1800000, ￥, ja-JP            => Output: ￥1.8M
   * Input: 10700000000, ₩, ko-KR         => Output: ₩10.7B
   */
  static getTruncated(number, { currencyCode, locale }) {
    const symbol = Money.getCurrencySymbol(currencyCode, locale);
    if (Number.isNaN(Number(number))) {
      return '';
    }

    if (number >= 1e3 && number < 1e6) {
      return `${symbol}${+(number / 1e3).toFixed(1)}K`;
    }

    if (number >= 1e6 && number < 1e9) {
      return `${symbol}${+(number / 1e6).toFixed(1)}M`;
    }

    if (number >= 1e9) {
      return `${symbol}${+(number / 1e9).toFixed(1)}B`;
    }

    return `${symbol}${number}`;
  }

  static getPercentage(number, noZeroOrNegativeOne = false) {
    let retVal = '--%';
    if (Money.isNumeric(number)) {
      if ((number === 0 || number === -1) && noZeroOrNegativeOne) {
        return retVal;
      }

      if (Math.abs(number) < 0.1 && number !== 0) {
        retVal = numeral(number).format('0.0%');
      } else {
        retVal = numeral(number).format('0%');
      }
    }

    return retVal;
  }

  static getLocaleString(number, locale) {
    const parsedNumber = parseInt(number, 10);
    return parsedNumber || parsedNumber === 0
      ? parsedNumber.toLocaleString(locale)
      : '';
  }

  static isNumeric(proposedNumber) {
    const parsedNumber = parseFloat(proposedNumber);
    return !Number.isNaN(parsedNumber) && Number.isFinite(parsedNumber);
  }

  static convertFullToHalf(number) {
    return number.replace(/[\uFF10-\uFF19]/g, (m) =>
      String.fromCharCode(m.charCodeAt(0) - 0xfee0),
    );
  }
}

export default Money;
